import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import React, { useEffect, useState } from "react";
import {
  fetchCourseLearners,
} from "src/redux/actions/coaching/learners";

import { TabBar } from "src/components/tabbar";
import InfiniteScroll from "src/components/infiniteScroll";
import Thumbnail from "src/components/thumbnail";

import cx from "classnames";
import _ from "lodash";
import styles from "./learners.module.scss";
import { AddLearnersDialog } from "./add";

interface CourseLearnersProps {
  id: string;
}

export const CourseLearners: React.FC<CourseLearnersProps> = ({ id }) => {
  const dispatch = useDispatch();

  const { loading, learners, totalRecordsCount, currentPage, nextPage, hasMoreItems } = useSelector(
    (state: any) => {
      const learnersState = _.get(state, "courses.learners");
      const loading = _.get(learnersState, "loading");
      const pagination = _.get(learnersState, "pagination");
      const totalRecordsCount = _.get(pagination, "totalRecordsCount", 0);
      const currentPage = _.get(pagination, "currentPage", 0);
      const total = _.get(pagination, "totalPages", 0);
      const hasMoreItems = _.get(pagination, "hasMoreItems");

      const learners = _.get(learnersState, "items") || [];

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading: loading,
        learners: learners,
        totalRecordsCount: totalRecordsCount,
        currentPage: currentPage,
        nextPage: nextPage,
        hasMoreItems: hasMoreItems,
      };
    },
    _.isEqual,
  );

  const [activeTab, setActiveTab] = useState(0);
  const [showAddLearner, setShowAddLearner] = useState(false);

  const handleSelectionChange = (index: number) => {
    setActiveTab(index);

    let status = "ALL";
    switch (index) {
      case 0:
        status = "ALL";
        break;

      case 1:
        status = "COMPLETED";
        break;

      case 2:
        status = "NOT_STARTED";
        break;
      default:
        break;
    }

    if (id) {
      const request = {
        course: id,
        status: status,
        page: 0,
        limit: 10,
      };
      dispatch(fetchCourseLearners(request));
    }
  };

  const handleLoadMoreLearners = () => {

    if (nextPage && id) {
      let status = "";
      switch (activeTab) {
        case 0:
          status = "ALL";
          break;

        case 1:
          status = "COMPLETED";
          break;

        case 2:
          status = "NOT_STARTED";
          break;
        default:
          break;
      }

      const request = {
        course: id,
        status: status,
        page: nextPage,
        limit: 10,
      };
      dispatch(fetchCourseLearners(request));
    }
  };

  const handleAddLearnersFinished = () => {
    setShowAddLearner(false);
    handleSelectionChange(0);
  }

  return (
    <div className={styles.learnersContainer}>
      <div className={styles.header}>
        <span className={styles.title}>Learners ({totalRecordsCount})</span>
        <div
          className={styles.addLearner}
          onClick={() => setShowAddLearner(true)}
        >
          <i className="paperflite-add" />
          <span>Add learners</span>
        </div>
      </div>
      <div className={styles.tabsContainer}>
        <TabBar
          className={cx(styles.tabs)}
          gap={32}
          tabSize="equal"
          active={activeTab}
          onSelectionChange={(action) => handleSelectionChange(action)}
        >
          <span className={cx(styles.tab, { [styles.active]: activeTab === 0 })}>
            All learners
          </span>
          <span className={cx(styles.tab, { [styles.active]: activeTab === 1 })}>
            Completed
          </span>
          <span className={cx(styles.tab, { [styles.active]: activeTab === 2 })}>
            Not started
          </span>
        </TabBar>
      </div>
      <div className={styles.stacks}>
        <InfiniteScroll
          className={styles.scroll}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={handleLoadMoreLearners}
          loader={() => {
            return _.times(_.size(learners) === 0 || currentPage === 0 ? 2 : 1, (index) => (
              <LearnerPlaceholder key={index} />
            ));
          }}
        >
          {_.size(learners) === 0 && !loading && (
            <span className={styles.noRecordsFound}>No learners.</span>
          )}

          <div className={styles.learnersList}>
            {learners.map((learner, index) => (
              <div key={index} className={styles.learner}>
                <div className={styles.details}>
                  <Thumbnail
                    media={learner.user?.icon}
                    defaultImage="/images/assets/default_asset.jpg"
                    className={styles.icon}
                  />
                  <span className={styles.name}>{learner.user.firstName || ""} {learner.user.lastName}</span>
                </div>
              </div>
            ))}
          </div>
        </InfiniteScroll>
      </div>

      {showAddLearner && (
        <AddLearnersDialog
          show={showAddLearner}
          onFinish={handleAddLearnersFinished}
          onClose={() => setShowAddLearner(false)}
        />
      )}
    </div>
  );
};


export const LearnerPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.learnerPlaceholder)}>
      <div className={styles.name}></div>
      <div className={styles.block}></div>
    </div>
  );
};
